import ErrorMessage from "components/ErrorMessage";
import ODateTimePicker from "components/shared/datePicker/ODateTimePicker";
import AuthContext from "context/AuthContext";
import { isEmpty } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TiTrash } from "react-icons/ti";
import { useNavigate, useParams } from "react-router-dom";
import { apiGet, apiPost, apiPut } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import helpers from "utils/helpers";
import Input from "../../../components/Input";
import QuestionDialog from "../../../components/QuestionDialog";
import { Button } from "@mui/material";
import useToastContext from "hooks/useToastContext";
import { QUIZ_TEST_CATEGORIES } from "utils/constants";

function TestEditPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [test, setTest] = useState(null);
  const notification = useToastContext();
  const [title, setTitle] = useState(test?.title || "");
  const [titleAr, setTitleAr] = useState(test?.titleAr || "");
  const [questions, setQuestions] = useState(test?.questions || []);
  const [startDate, setStartDate] = useState(test?.startTime || "");
  const [endDate, setEndDate] = useState(test?.endTime || "");
  const [description, setDescription] = useState(test?.description || "");
  const [descriptionAr, setDescriptionAr] = useState(test?.descriptionAr || "");
  const [attempts, setAttempts] = useState(
    test?.attempts ? test.attempts + "" : "1",
  );
  const [duration, setDuration] = useState(
    test?.duration ? test.duration + "" : "",
  );
  const [showTimer, setShowTimer] = useState(false);
  const [passingPercentage, setPassingPercentage] = useState("");
  const [status, setStatus] = useState(test?.status || "active");
  const [testType, setTestType] = useState(test?.testType || "quiz");
  const [category, setCategory] = useState(test?.category || null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchTest() {
      try {
        const path = apiPath.quizTests + "/" + id;
        const { data } = await apiGet(path);
        setPassingPercentage(data?.results?.passingPercentage || "");
        setTest(data?.results);
        setTitle(data?.results?.title || "");
        setTitleAr(data?.results?.titleAr || "");
        setDescription(data?.results?.description || "");
        setDescriptionAr(data?.results?.descriptionAr || "");
        setStartDate(data?.results?.startTime || "");
        setEndDate(data?.results?.endTime || "");
        setAttempts(data?.results?.attempts ? data.results.attempts + "" : "1");
        setDuration(data?.results?.duration ? data.results.duration + "" : "");
        setShowTimer(data?.results?.showTimer ?? false);
        setStatus(data?.results?.status || "active");
        setTestType(data?.results?.testType || "quiz");
        setQuestions(data?.results?.questions || []);
        setCategory(data?.results?.category || null);
      } catch (error) {
        console.log(error);
        notification.error(error?.response?.data?.message);
      }
    }
    fetchTest();
  }, [id, notification]);

  function questionAddedHandler(question) {
    setQuestions([...questions, question]);
    setOpen(false);
  }

  function deleteQuestionHandler(index) {
    setQuestions(questions.filter((q, i) => i !== index));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid()) return;
    const path = apiPath.quizTests + "/" + id;
    try {
      const payload = {
        title,
        titleAr,
        description,
        descriptionAr,
        startTime: new Date(startDate),
        endTime: new Date(endDate),
        attempts,
        passingPercentage,
        status,
        testType,
        duration,
        showTimer,
        category: testType === "practice" ? category : null,
      };

      const response = await apiPut(path, payload);
      if (!response?.data?.success) return;

      const testId = response?.data?.results?._id;

      for (const question of questions) {
        if (question._id) continue;
        const { data } = await apiPost(apiPath.quizQuestions, {
          testId,
          title: question.title,
          titleAr: question.titleAr,
          description: question.description,
          descriptionAr: question.descriptionAr,
          type: question.type,
          points: question.points,
          expectedAnswer: question.expectedAnswer,
          options: question.options,
          matchingPairs: question.matchingPairs,
          testType,
        });
        if (!data?.success) return;
      }
      notification.success(t("O_TEST_UPDATED_SUCCESSFULLY"));
      navigate("/test-manager/tests");
    } catch (error) {
      console.log(error);
    }
  }

  function isValid() {
    const passingPercentageValid =
      passingPercentage === "" ||
      (passingPercentage > 0 && passingPercentage <= 100);
    const isStartBeforeEnd =
      startDate && endDate && new Date(startDate) < new Date(endDate);
    const isCategoryValid = testType !== "practice" || !isEmpty(category);
    return !(
      isEmpty(title) ||
      isEmpty(titleAr) ||
      isEmpty(description) ||
      isEmpty(descriptionAr) ||
      !startDate ||
      !endDate ||
      !isStartBeforeEnd ||
      isEmpty(attempts) ||
      isEmpty(duration) ||
      isEmpty(questions) ||
      !passingPercentageValid ||
      !isCategoryValid
    );
  }

  return (
    <div className="bg-white px-3 py-4 ">
      <h1 className="text-2xl font-bold">Edit Test</h1>
      <hr className="my-4" />
      <form className="flex flex-col gap-4 " onSubmit={handleSubmit}>
        <div className="flex gap-4">
          <Input
            label={t("O_TITLE")}
            placeholder={t("O_TITLE")}
            required
            name="title"
            onChange={(e) => setTitle(e.target.value)}
            value={title}
          />
          <Input
            label={t("O_TITLE") + t("O_ARABIC")}
            placeholder={t("O_TITLE") + t("O_ARABIC")}
            required
            name="titleAr"
            value={titleAr}
            onChange={(e) => setTitleAr(e.target.value)}
          />
        </div>
        <div className="flex gap-4">
          <Input
            label={t("O_DESCRIPTION")}
            placeholder={t("O_DESCRIPTION")}
            required
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Input
            label={t("O_DESCRIPTION") + t("O_ARABIC")}
            placeholder={t("O_DESCRIPTION") + t("O_ARABIC")}
            required
            name="descriptionAr"
            value={descriptionAr}
            onChange={(e) => setDescriptionAr(e.target.value)}
          />
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <ODateTimePicker
              name="startDate"
              placeholder={t("O_START_TIME")}
              value={startDate}
              handleDateChange={setStartDate}
              inputLabel={t("O_START_TIME")}
              startDate={startDate}
              maxDate={endDate}
            />
          </div>
          <div className="flex-1">
            <ODateTimePicker
              name="endDate"
              placeholder={t("O_END_TIME")}
              value={endDate}
              handleDateChange={setEndDate}
              inputLabel={t("O_END_TIME")}
              startDate={endDate}
              minDate={startDate || "today"}
            />
            {startDate &&
              endDate &&
              new Date(endDate) <= new Date(startDate) && (
                <ErrorMessage
                  message={
                    t("O_END_TIME_MUST_BE_AFTER_START_TIME") ||
                    "End time must be after start time"
                  }
                />
              )}
          </div>
        </div>

        <div className="flex  gap-2">
          <Input
            type="text"
            label={t("O_NUMBER_OF_ATTEMPTS")}
            required
            name="attempts"
            value={attempts + ""}
            onChange={(e) => {
              const value = e.target.value;
              if (!value.match(/^[0-9]*$/)) {
                return;
              }

              setAttempts(value);
            }}
            disabled={testType === "quiz"}
          />
          <Input
            type="text"
            label={t("O_DURATION_MINUTES")}
            required
            name="duration"
            value={duration + ""}
            onChange={(e) => {
              const value = e.target.value;
              if (!value.match(/^[0-9]*$/)) {
                return;
              }
              if (+value < 1) {
                setDuration("");
                return;
              }
              setDuration(value);
            }}
          />
          <div className="py-3 mb-1 flex-1">
            <span className="block mb-2 text-sm font-medium text-gray-900">
              Show timer
            </span>
            <label
              htmlFor="showTimer"
              className="flex items-center gap-3 w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg cursor-pointer select-none transition-colors hover:bg-gray-100 focus-within:border-black focus-within:ring-0"
            >
              <input
                type="checkbox"
                name="showTimer"
                id="showTimer"
                checked={showTimer}
                onChange={(e) => setShowTimer(e.target.checked)}
                className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 accent-blue-600 focus:outline-none focus:ring-0 focus:ring-offset-0"
              />
              <span className="text-sm font-medium text-gray-700">
                {showTimer ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="testType">{t("O_TEST_TYPE")}</label>
          <select
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            name="testType"
            id="testType"
            onChange={(e) => {
              const value = e.target.value;

              setAttempts("1");
              setTestType(value);

              if (value !== "practice") {
                setCategory(null);
              }
            }}
            value={testType}
          >
            <option value="quiz">{t("O_QUIZ")}</option>
            <option value="practice">{t("O_PRACTICE")}</option>
          </select>
        </div>

        {testType === "practice" && (
          <div className="flex flex-col gap-2">
            <label htmlFor="category">{t("O_CATEGORY")}</label>
            <select
              required={testType === "practice"}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              name="category"
              id="category"
              onChange={(e) =>
                setCategory(e.target.value === "" ? null : e.target.value)
              }
              value={category ?? ""}
            >
              <option value="">{t("O_SELECT_CATEGORY")}</option>

              {QUIZ_TEST_CATEGORIES.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.title}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex flex-col gap-2">
          <label htmlFor="status">{t("O_STATUS")}</label>
          <select
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            name="status"
            id="status"
            onChange={(e) => setStatus(e.target.value)}
            value={status}
          >
            <option value="active">{t("O_ACTIVE")}</option>
            <option value="inactive">{t("O_INACTIVE")}</option>
          </select>
        </div>
        <div className="flex flex-col gap-2">
          <Input
            label={"Passing Percentage"}
            placeholder={"Passing Percentage"}
            required
            name="passingPercentage"
            value={passingPercentage}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") {
                setPassingPercentage("");
                return;
              }
              if (!value.match(/^[0-9]*$/)) {
                return;
              }
              if (+value < 0) {
                setPassingPercentage("0");
              } else if (+value > 100) {
                setPassingPercentage("100");
              } else {
                setPassingPercentage(value);
              }
            }}
          />
        </div>
        <hr className="my-4" />

        <div className="flex flex-col gap-4">
          <QuestionsTable
            questions={questions}
            deleteQuestionHandler={deleteQuestionHandler}
          />
        </div>
        <Button
          type="button"
          variant="contained"
          className="self-center"
          sx={{
            backgroundColor: "#8FC150",
            "&:hover": { backgroundColor: "#7eae43" },
          }}
          onClick={() => setOpen(true)}
        >
          {t("O_ADD_QUESTION")}
        </Button>
        <QuestionDialog
          questionAddedHandler={questionAddedHandler}
          closeHandler={() => setOpen(false)}
          open={open}
        />
        <hr className="my-4" />
        <button
          type="submit"
          disabled={!isValid()}
          className={`bg-[#8FC150] hover:bg-[#7eae43] transition-colors duration-150 text-white px-4 py-2 rounded-md text-nowrap self-center disabled:bg-gray-500 disabled:hover:bg-gray-500 ${
            !isValid() ? "bg-gray-500" : ""
          }`}
        >
          {t("O_UPDATE_TEST")}
        </button>
      </form>
    </div>
  );
}

export default TestEditPage;

function QuestionsTable({ questions, deleteQuestionHandler }) {
  const { t } = useTranslation();
  if (questions?.length === 0 || isEmpty(questions)) return null;
  return (
    <table className="w-full text-sm text-left text-black  ">
      <thead className="text-sm   border border-[#E1E6EE]  bg-gray-700  text-white">
        <tr className="py-3 px-6">
          <th className="py-3 px-6">{t("O_TITLE")}</th>
          <th className="py-3 px-6">{t("O_TITLE") + t("O_ARABIC")}</th>
          <th className="py-3 px-6">{t("O_DESCRIPTION")}</th>
          <th className="py-3 px-6">{t("O_DESCRIPTION") + t("O_ARABIC")}</th>
          <th className="py-3 px-6">{t("O_TYPE")}</th>
          <th className="py-3 px-6">{t("O_POINTS")}</th>
          <th className="py-3 px-6">{t("O_ACTION")}</th>
        </tr>
      </thead>
      <tbody>
        {questions.map((question, index) => (
          <tr className="py-3 px-6 border-b border-[#E1E6EE]" key={index}>
            <td className="py-3 px-6">{question.title || "-"}</td>
            <td className="py-3 px-6">{question.titleAr || "-"}</td>
            <td className="py-3 px-6">{question.description || "-"}</td>
            <td className="py-3 px-6">{question.descriptionAr || "-"}</td>
            <td className="py-3 px-6">{question.type || "-"}</td>
            <td className="py-3 px-6">{question.points || "-"}</td>
            <td className="py-3 px-6 flex gap-2">
              <button
                type="button"
                className="bg-red-500 hover:bg-red-700 transition-colors duration-150 text-white px-4 py-2 rounded-md"
                onClick={() =>
                  helpers.alertFunction(
                    `Are you sure want to delete ${
                      question.title || "this question"
                    }?`,
                    index,
                    deleteQuestionHandler,
                    true,
                  )
                }
              >
                <TiTrash />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
